import { useEffect, useState } from "react";
import {
  Colors,
  Icon,
  Shadows,
  TooltipWrapper,
  Zindexes,
} from "@zapier/design-system";
import { css } from "@emotion/react";
import { CurrentAccountIdProvider } from "@zapier/identity";

import { ActionsListView } from "./ActionsListView";
import { ActionsList } from "../ActionsList";
import { Popover } from "../Popover";

import { emitEvent } from "@zapier/trials/src/utils";
import { InteractionName } from "@zapier/trials/src/utils/emitEvent/trackingTypes";
import { Account } from "@zapier/trials/src/types";

const Styles = {
  root: css({
    backgroundColor: Colors.neutral100,
    borderRadius: "5px",
    border: `1px solid ${Colors.neutral300}`,
    boxShadow: Shadows.elevation5,
    maxHeight: "550px",
    overflow: "hidden",
    width: "550px",
    zIndex: Zindexes.universalNav + 1,

    // Sidebar positioning
    position: "absolute",
    right: 0,
    transform: "translate(calc(100% + 12px), -100%)",
  }),

  rootActions: css({
    display: "flex",
    position: "absolute",
    top: "8px",
    right: "8px",

    "& button": {
      backgroundColor: "transparent",
      border: "none",
      color: Colors.neutral500,
      padding: 0,

      "&:hover, &:focus": {
        color: Colors.neutral100,
        outline: "none",
      },
    },
  }),
};

type Props = {
  /**
   * Called when the "X" close button is clicked or when the user clicks outside the component.
   */
  onClose: () => void;

  /**
   * The currently active account for the logged-in user.
   */
  account: Account;

  /**
   * Builder.io API key used to fetch trial actions.
   */
  builderApiKey: string;
};

export const TrialHub = ({ onClose, builderApiKey, account }: Props) => {
  const [activeView] = useState("actions-list");

  useEffect(() => {
    emitEvent(InteractionName.trialHubRendered, {
      event_action: "in_view",
      option_selected: "",
    });
  }, []);

  const renderView = () => {
    switch (activeView) {
      case "actions-list":
        return (
          <ActionsListView trialEndDate={account?.trialEndDate}>
            <ActionsList account={account} builderApiKey={builderApiKey} />
          </ActionsListView>
        );
    }
  };

  const handleOnClose = () => {
    onClose();
    emitEvent(InteractionName.trialHubClosed, {
      event_action: "close",
      option_selected: "",
    });
  };

  return (
    <CurrentAccountIdProvider>
      <Popover
        ariaLabel="Trial Hub"
        css={Styles.root}
        onClickOutside={handleOnClose}
      >
        {renderView()}

        <div css={Styles.rootActions}>
          <TooltipWrapper content="Close" position="southeast">
            {({ childProps }) => (
              <button
                onClick={handleOnClose}
                aria-label="Close"
                aria-describedby={childProps.ariaDescribedBy}
              >
                <Icon isBlock={true} name="formX" size={32} />
              </button>
            )}
          </TooltipWrapper>
        </div>
      </Popover>
    </CurrentAccountIdProvider>
  );
};
